import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ArrowSquareOut, Phone, MapPin, IdentificationCard, FileText, Buildings } from "@phosphor-icons/react";

/**
 * Panel lateral rápido con resumen del expediente.
 * Permite consultar datos clave sin salir de la página actual (p.ej. /rutas).
 *
 * Props:
 *  - expedienteId: string | null. Si null, el sheet está cerrado.
 *  - onClose: () => void
 */
export default function ExpedienteQuickView({ expedienteId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!expedienteId) {
      setData(null);
      return;
    }
    setLoading(true);
    api.get(`/expedientes/${expedienteId}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ _error: true }))
      .finally(() => setLoading(false));
  }, [expedienteId]);

  const open = Boolean(expedienteId);
  const ed = data?.extracted_data || {};
  const fullAddress = [ed.direccion_riesgo, ed.codigo_postal, ed.municipio || ed.poblacion, ed.provincia]
    .filter(Boolean).join(", ");

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent className="sm:max-w-md w-full overflow-y-auto" data-testid="exp-quick-view">
        <SheetHeader className="text-left">
          <div className="text-[10px] uppercase tracking-wider font-bold text-orange-600">
            {data?.detected_aseguradora || "Expediente"}
          </div>
          <SheetTitle className="text-lg leading-tight pr-6">
            {ed.asegurado_nombre || "(sin nombre)"}
          </SheetTitle>
          <SheetDescription className="font-mono text-[11px]">
            {ed.numero_siniestro || ed.numero_siniestro_gabinete || "—"}
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="py-10 text-center text-xs text-muted">Cargando…</div>
        )}

        {data?._error && (
          <div className="py-10 text-center text-xs text-rose-600">
            No se pudo cargar el expediente.
          </div>
        )}

        {data && !data._error && (
          <div className="mt-5 space-y-4 text-sm">
            {/* Dirección */}
            {fullAddress && (
              <Section icon={<MapPin className="w-3.5 h-3.5 text-orange-600" weight="bold" />} title="Dirección del riesgo">
                <div className="text-foreground">{fullAddress}</div>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] text-orange-600 hover:underline inline-flex items-center gap-1 mt-1"
                  data-testid="qv-maps"
                >
                  Abrir en Google Maps <ArrowSquareOut className="w-3 h-3" weight="bold" />
                </a>
              </Section>
            )}

            {/* Contacto */}
            <Section icon={<Phone className="w-3.5 h-3.5 text-orange-600" weight="bold" />} title="Contacto del asegurado">
              <Row label="Móvil" value={ed.asegurado_movil} link={ed.asegurado_movil ? `tel:${ed.asegurado_movil}` : null} />
              <Row label="Teléfono" value={ed.asegurado_telefono} link={ed.asegurado_telefono ? `tel:${ed.asegurado_telefono}` : null} />
              <Row label="Email" value={ed.asegurado_email} link={ed.asegurado_email ? `mailto:${ed.asegurado_email}` : null} />
              <Row label="NIF" value={ed.asegurado_nif} />
            </Section>

            {/* Datos del siniestro */}
            <Section icon={<FileText className="w-3.5 h-3.5 text-orange-600" weight="bold" />} title="Siniestro">
              <Row label="Tipo encargo" value={ed.tipo_encargo} highlight={String(ed.tipo_encargo || "").toLowerCase().startsWith("presencial") ? "orange" : null} />
              <Row label="Fecha siniestro" value={ed.fecha_siniestro} />
              <Row label="Fecha encargo" value={ed.fecha_encargo} />
              <Row label="Causa" value={ed.causa} />
              <Row label="Ramo" value={ed.ramo} />
              <Row label="Reserva" value={data.importe_reserva != null ? `${data.importe_reserva} €` : null} />
            </Section>

            {/* Póliza */}
            <Section icon={<IdentificationCard className="w-3.5 h-3.5 text-orange-600" weight="bold" />} title="Póliza">
              <Row label="Nº póliza" value={ed.numero_poliza} mono />
              <Row label="Nº siniestro" value={ed.numero_siniestro} mono />
              {ed.numero_siniestro_gabinete && (
                <Row label="Nº siniestro (gabinete)" value={ed.numero_siniestro_gabinete} mono />
              )}
              <Row label="Capital continente" value={ed.capital_continente ? `${ed.capital_continente} €` : null} />
              <Row label="Capital contenido" value={ed.capital_contenido ? `${ed.capital_contenido} €` : null} />
            </Section>

            {/* Observaciones */}
            {ed.observaciones && (
              <Section icon={<Buildings className="w-3.5 h-3.5 text-orange-600" weight="bold" />} title="Observaciones">
                <div className="text-xs whitespace-pre-wrap text-foreground">{ed.observaciones}</div>
              </Section>
            )}

            {/* CTA — abrir review completa */}
            <Link
              to={`/review/${expedienteId}`}
              className="btn-brand w-full text-center text-xs px-4 py-3 rounded-md font-semibold inline-flex items-center justify-center gap-1.5 mt-2"
              data-testid="qv-open-full"
            >
              Abrir expediente completo
              <ArrowSquareOut className="w-3.5 h-3.5" weight="bold" />
            </Link>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

const Section = ({ icon, title, children }) => (
  <div className="border border-border rounded-md p-3 bg-white">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-muted mb-2">
      {icon}{title}
    </div>
    <div className="space-y-1">{children}</div>
  </div>
);

const Row = ({ label, value, link, mono, highlight }) => {
  if (!value) return null;
  const valueClass = `text-xs ${mono ? "font-mono" : ""} ${highlight === "orange" ? "text-orange-700 font-semibold" : "text-foreground"}`;
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[10px] text-muted shrink-0">{label}</span>
      {link
        ? <a href={link} className={`${valueClass} hover:underline text-right truncate`} data-testid={`qv-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</a>
        : <span className={`${valueClass} text-right truncate`}>{value}</span>}
    </div>
  );
};
